import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Nav from './Nav';
import SearchBox from './SearchBox';

const TOP_LINKS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Home', end: true },
  { to: '/infrastructure', label: 'Infrastructure' },
  { to: '/application', label: 'Application' },
  { to: '/developer-portal', label: 'Developer Portal' },
  { to: '/documentation', label: 'Documentation' },
  { to: '/data-dictionary', label: 'Data Dictionary' },
  { to: '/releases', label: 'Releases' },
  { to: '/architecture', label: 'Architecture' },
  { to: '/salesforce', label: 'Salesforce' },
  { to: '/azure', label: 'Azure' },
  { to: '/semarchy', label: 'Semarchy' },
  { to: '/netsuite', label: 'NetSuite' },
  { to: '/creatio', label: 'Creatio' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/environments', label: 'Environments' },
];

// Routes where the "Dashboard" section in the top nav should appear active.
// Anything under /test, /dev, /uat, /prod is part of the dashboard view.
const ENV_ROUTE_PREFIXES = ['/test', '/dev', '/uat', '/prod', '/dashboard'];

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isInDashboard = ENV_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  return (
    <div className="app-shell">
      <nav className="app-topnav" aria-label="Site sections">
        <div className="app-topnav-links">
          {TOP_LINKS.map((l) => {
            // Special-case Dashboard: highlight it whenever the user is on
            // any of the per-env routes, not just /dashboard itself.
            if (l.to === '/dashboard') {
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={isInDashboard ? 'active' : undefined}
                >
                  {l.label}
                </NavLink>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) => (isActive ? 'active' : undefined)}
              >
                {l.label}
              </NavLink>
            );
          })}
        </div>
        <div className="app-topnav-right">
          <SearchBox />
          <span className="app-topnav-meta">Synergy Platform</span>
        </div>
      </nav>

      <header className="app-hero">
        <div className="app-hero-inner">
          <h1>Synergy Platform</h1>
          <p className="app-hero-sub">
            Master record for the Synergy integration platform &mdash; documentation,
            developer portal, and live operational dashboards across TEST / DEV / UAT / PROD.
          </p>
        </div>
      </header>

      {isInDashboard && <Nav />}

      <main className="app-main">{children}</main>

      <footer className="app-footer">
        <span>Protected by Entra ID SSO &mdash; CIPD tenant users only</span>
        <span className="app-footer-deployed" title={__BUILD_TIME__}>
          Site deployed{' '}
          {new Date(__BUILD_TIME__).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
        <a href="#/site">Site</a>
        <a href="/.auth/logout">Sign out</a>
      </footer>
    </div>
  );
}
